import { zodResolver } from '@hookform/resolvers/zod';
import { addPositiveToast } from 'modules/toastContainer';
import { useMemo } from 'react';
import { Controller, useForm, UseFormSetValue } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { Modal, MODAL_SIZES, MODAL_VARIANTS, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { z } from 'zod';
import { FIELD_NAMES } from '../../constants';
import { ProjectSettingsFormSchemaType, useProjectSettingsModalsContext } from '../../contexts';
import { createPositiveToastMessage, getUniqueLocales } from '../../utils';
import { MarkdownDescription } from '../ProjectSettingsForm/ConnectedFormField';

const { LOCALES } = FIELD_NAMES;
const DEFAULT_ADD_LOCALE_FORM_VALUES = { newLocale: '' };

export const ADD_LOCALE_MODAL_TEXT = {
  labelDescriptionText:
    'Enter a name for your locale. We recommend using a two-letter ISO code for clarity and consistency.',
  labelDescriptionTextId: 'add-locale-modal-description',
  labelDescriptionTextSupportUrl: 'https://support.iterable.com/hc/en-us/articles/213279923',
};

const getAddLocaleFormSchema = (locales: string[]) =>
  z.object({
    newLocale: z
      .string()
      .min(1, 'Name is required')
      .refine(
        (val: string) => !locales.some((locale) => locale === val.trim()),
        'A locale with the same name already exists in your project.',
      ),
  });

type AddLocaleModalProps = {
  /** Current locales provided by project.extendedProjectSettings */
  locales: string[];
  /** Utility function provided by Project Settings page form context */
  setValue: UseFormSetValue<ProjectSettingsFormSchemaType>;
};

export const AddLocaleModal = ({ locales, setValue }: AddLocaleModalProps) => {
  const dispatch = useDispatch();
  const { hideModal } = useProjectSettingsModalsContext();
  const schema = useMemo(() => getAddLocaleFormSchema(locales), [locales]);

  /** Local form used for adding `locales` to existing values */
  const {
    control,
    formState: { isSubmitted, isValid } = {},
    handleSubmit,
  } = useForm({
    defaultValues: DEFAULT_ADD_LOCALE_FORM_VALUES,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
  });

  const onSubmit = (fields: { newLocale: string }) => {
    const newLocale = fields.newLocale?.trim();
    const uniqueLocales = getUniqueLocales(locales, newLocale);

    setValue(LOCALES, uniqueLocales, { shouldDirty: true });
    const message = createPositiveToastMessage('locale', 'added');
    dispatch(addPositiveToast(message));
    hideModal();
  };

  return (
    <Modal
      id="add-locale-modal"
      onClose={hideModal}
      isModalOpen={true}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>Add new locale</ModalHeader>
      <ModalBody>
        <form id="add-new-locale-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="newLocale"
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const errorMessage = error?.message || '';
              return (
                <FormField
                  inputId="name"
                  labelText="Name"
                  hasError={!!errorMessage}
                  errorText={errorMessage}
                  descriptionText={
                    <MarkdownDescription
                      markdownText={ADD_LOCALE_MODAL_TEXT.labelDescriptionText}
                      id={ADD_LOCALE_MODAL_TEXT.labelDescriptionTextId}
                      supportUrl={ADD_LOCALE_MODAL_TEXT.labelDescriptionTextSupportUrl}
                    />
                  }
                >
                  <InputText name="name" id="name" value={value} onChange={onChange} autoFocus />
                </FormField>
              );
            }}
          />
        </form>
      </ModalBody>
      <ModalFooter onClose={hideModal}>
        <PrimaryButton
          form="add-new-locale-form"
          isDisabled={!!isSubmitted && !isValid}
          size={BUTTON_SIZES.LARGE}
          type="submit"
        >
          Add locale
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
